
import React from 'react';
import { Users, Calculator, FileText, CheckCircle2, DollarSign, BookOpen, Star, ArrowRight, Github, Heart, MapPin } from 'lucide-react';
import { TabType } from '../types';
import { FakeUserCounter } from './FakeUserCounter';
import { Logo } from './Logo';

interface HomeViewProps {
  setTab: (tab: TabType) => void;
}

export const HomeView: React.FC<HomeViewProps> = ({ setTab }) => {
  return (
    <div className="animate-fadeIn w-full overflow-hidden">
      {/* Maintenance Banner */}
      <div className="bg-[#800020] text-white py-3 px-4 text-center font-bold text-sm md:text-base shadow-md relative z-50">
        <p>😊 THANK YOU FOR YOUR PATIENCE.</p>
        <p className="text-xs md:text-sm mt-1 opacity-90 uppercase">आपका सुझाव एवं सलाह हमारे लिय महत्वपूर्ण है</p>
        <p className="text-xs md:text-sm opacity-90 uppercase">-&gt;9229001900(WHATSAPP ONLY)✌🏼</p>
      </div>

      {/* Hero Section */}
      <div className="bg-gradient-to-b from-orange-50 to-white relative py-12 md:py-32">
        <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-20">
            <div className="absolute -top-20 -left-20 w-64 md:w-96 h-64 md:h-96 bg-orange-400 rounded-full blur-[80px] md:blur-[120px] animate-blob"></div>
            <div className="absolute top-1/4 -right-20 w-64 md:w-96 h-64 md:h-96 bg-red-400 rounded-full blur-[80px] md:blur-[120px] animate-blob animation-delay-2000"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-6 relative z-10 text-center">
            <div className="flex justify-center mb-6 md:mb-8">
              <Logo size={80} className="md:w-[120px] md:h-[120px] drop-shadow-2xl animate-float" />
            </div>
            <div className="inline-flex items-center bg-white border border-orange-200 text-orange-700 px-4 md:px-5 py-1.5 md:py-2 rounded-full text-[10px] md:text-xs font-bold mb-6 md:mb-8 shadow-sm">
              <span className="relative flex h-2 w-2 mr-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-orange-500"></span>
              </span>
              BR TEACHER PORTAL
            </div>
            
            <h2 className="text-3xl sm:text-5xl md:text-8xl font-black text-gray-900 mb-4 md:mb-8 leading-[1.1] tracking-tighter">
              आप सभी <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-600 via-orange-600 to-yellow-500">TEACHER</span> <br /> 
              का स्वागत है।
            </h2>
            
            <p className="max-w-2xl mx-auto text-gray-600 text-sm sm:text-lg md:text-2xl mb-6 md:mb-12 font-medium leading-relaxed px-2 md:px-0">
              BR TEACHER — आपकी सेवा, वेतन गणना और आधिकारिक संसाधनों के लिए एक मात्र विश्वसनीय स्थान।
            </p>
            
            <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-6 justify-center items-center px-4 md:px-0">
              <button 
                onClick={() => setTab('arrear')} 
                className="group w-full sm:w-auto bg-orange-600 text-white px-6 md:px-12 py-3.5 md:py-5 rounded-xl md:rounded-[2.5rem] font-black text-base md:text-xl hover:bg-orange-700 shadow-2xl shadow-orange-200 transition-all transform hover:-translate-y-2 flex items-center justify-center"
              >
                <Calculator size={20} className="md:w-[24px] md:h-[24px] mr-2 md:mr-3" />
                Arrear Calculator खोलें
                <ArrowRight size={18} className="md:w-[20px] md:h-[20px] ml-1.5 md:ml-2 group-hover:translate-x-2 transition-transform" />
              </button>
              <button 
                onClick={() => setTab('tre3')} 
                className="w-full sm:w-auto bg-white text-gray-900 px-6 md:px-12 py-3.5 md:py-5 rounded-xl md:rounded-[2.5rem] font-black text-base md:text-xl hover:bg-gray-50 shadow-xl border-2 border-gray-100 transition-all transform hover:-translate-y-1"
              >
                महत्वपूर्ण फॉर्म्स
              </button>
            </div>
        </div>
      </div>

      {/* WE THE PEOPLE OF BIHAR BANNER */}
      <div className="w-full bg-slate-950 py-10 md:py-20 relative overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-orange-600 via-white to-green-600 opacity-50"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-orange-900/20 via-transparent to-transparent"></div>
        <div className="absolute -bottom-24 -right-24 w-64 h-64 bg-orange-600/10 rounded-full blur-[80px]"></div>
        
        <div className="max-w-7xl mx-auto px-4 md:px-6 relative z-10 text-center">
          <div className="flex flex-col items-center">
            <div className="mb-4 md:mb-6 flex items-center justify-center space-x-2">
               <div className="h-[1px] w-8 md:w-12 bg-orange-600"></div>
               <MapPin size={14} className="md:w-[16px] md:h-[16px] text-orange-600 animate-bounce" />
               <div className="h-[1px] w-8 md:w-12 bg-orange-600"></div>
            </div>
            <h2 className="text-2xl sm:text-4xl md:text-7xl lg:text-8xl font-black tracking-[0.05em] sm:tracking-[0.1em] md:tracking-[0.15em] text-white uppercase italic leading-tight">
              WE THE <span className="text-transparent bg-clip-text bg-gradient-to-b from-orange-400 to-orange-700 drop-shadow-[0_0_15px_rgba(234,88,12,0.4)]">PEOPLE</span> OF BIHAR
            </h2>
            <div className="mt-4 md:mt-6 text-orange-200/50 font-black tracking-[0.3em] md:tracking-[0.5em] text-[8px] md:text-xs uppercase">
              In Solidarity with the Teaching Community
            </div>
          </div>
        </div>
      </div>

      {/* Feature Section for Teachers */}
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-12 md:py-20">
        <div className="text-center mb-10 md:mb-16">
          <h3 className="text-2xl md:text-4xl font-black text-gray-900 mb-3 md:mb-4 tracking-tight">प्रमुख सुविधाएं</h3>
          <div className="w-16 md:w-24 h-1.5 md:h-2 bg-orange-500 mx-auto rounded-full"></div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {/* Arrear Card */}
          <div className="bg-white p-6 md:p-10 rounded-2xl md:rounded-[3rem] shadow-sm border border-gray-100 hover:shadow-2xl transition-all group">
            <div className="bg-orange-50 w-14 h-14 md:w-20 md:h-20 rounded-xl md:rounded-[2rem] flex items-center justify-center mb-4 md:mb-8 group-hover:scale-110 transition-transform">
              <Calculator size={28} className="text-orange-600 md:hidden" />
              <Calculator size={40} className="text-orange-600 hidden md:block" />
            </div>
            <h4 className="text-lg md:text-2xl font-black text-gray-900 mb-2 md:mb-4">Arrear Calculator</h4>
            <p className="text-gray-500 mb-4 md:mb-6 leading-relaxed text-xs md:text-base">
              BPSC और नियोजित शिक्षकों के लिए विशेष रूप से डिज़ाइन किया गया। अपने बेसिक पे और भत्तों के अंतर की सटीक गणना करें।
            </p>
            <button onClick={() => setTab('arrear')} className="text-orange-600 font-black flex items-center hover:underline text-xs md:text-base">
              गणना शुरू करें <ArrowRight size={16} className="md:w-[18px] md:h-[18px] ml-1" />
            </button>
          </div>

          {/* TDS Card */}
          <div className="bg-white p-6 md:p-10 rounded-2xl md:rounded-[3rem] shadow-sm border border-gray-100 hover:shadow-2xl transition-all group">
            <div className="bg-blue-50 w-14 h-14 md:w-20 md:h-20 rounded-xl md:rounded-[2rem] flex items-center justify-center mb-4 md:mb-8 group-hover:scale-110 transition-transform">
              <DollarSign size={28} className="text-blue-600 md:hidden" />
              <DollarSign size={40} className="text-blue-600 hidden md:block" />
            </div>
            <h4 className="text-lg md:text-2xl font-black text-gray-900 mb-2 md:mb-4">TDS & Income Tax</h4>
            <p className="text-gray-500 mb-4 md:mb-6 leading-relaxed text-xs md:text-base">
              शिक्षकों के लिए आयकर गणना और TDS फॉर्म्स का संग्रह। अपनी बचत और टैक्स की जानकारी यहाँ पायें।
            </p>
            <button onClick={() => setTab('tre2')} className="text-blue-600 font-black flex items-center hover:underline text-xs md:text-base">
              संसाधन देखें <ArrowRight size={16} className="md:w-[18px] md:h-[18px] ml-1" />
            </button>
          </div>

          {/* Rules Card */}
          <div className="bg-white p-6 md:p-10 rounded-2xl md:rounded-[3rem] shadow-sm border border-gray-100 hover:shadow-2xl transition-all group">
            <div className="bg-emerald-50 w-14 h-14 md:w-20 md:h-20 rounded-xl md:rounded-[2rem] flex items-center justify-center mb-4 md:mb-8 group-hover:scale-110 transition-transform">
              <BookOpen size={28} className="text-emerald-600 md:hidden" />
              <BookOpen size={40} className="text-emerald-600 hidden md:block" />
            </div>
            <h4 className="text-lg md:text-2xl font-black text-gray-900 mb-2 md:mb-4">सेवा नियमावली</h4>
            <p className="text-gray-500 mb-4 md:mb-6 leading-relaxed text-xs md:text-base">
              बिहार शिक्षक सेवा नियमावली 2024, अवकाश नियम और अन्य विभागीय अधिसूचनाओं की आधिकारिक प्रतियां डाउनलोड करें।
            </p>
            <button onClick={() => setTab('tre3')} className="text-emerald-600 font-black flex items-center hover:underline text-xs md:text-base">
              नियम पढ़ें <ArrowRight size={16} className="md:w-[18px] md:h-[18px] ml-1" />
            </button>
          </div>
        </div>
      </div>

      {/* Stats/Trust Bar */}
      <div className="bg-gray-900 py-10 md:py-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-12 text-center">
            <div>
              <div className="text-3xl md:text-5xl font-black text-orange-500 mb-1 md:mb-2">10K+</div>
              <div className="text-gray-400 font-bold uppercase text-[8px] md:text-xs tracking-widest">Active Teachers</div>
            </div>
            <div>
              <div className="text-3xl md:text-5xl font-black text-white mb-1 md:mb-2">50+</div>
              <div className="text-gray-400 font-bold uppercase text-[8px] md:text-xs tracking-widest">Official Forms</div>
            </div>
            <div>
              <div className="text-3xl md:text-5xl font-black text-white mb-1 md:mb-2">100%</div>
              <div className="text-gray-400 font-bold uppercase text-[8px] md:text-xs tracking-widest">Verified Data</div>
            </div>
        </div>
        <div className="mt-8 md:mt-12 flex justify-center px-4">
          <FakeUserCounter />
        </div>
      </div>

      {/* Footer Info */}
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-12 md:py-16">
        <div className="flex flex-col md:flex-row justify-between items-center gap-6 md:gap-8">
          <div className="flex items-center space-x-3 md:space-x-4">
            <Logo size={40} className="md:w-[48px] md:h-[48px] drop-shadow-lg" />
            <div>
              <h4 className="font-bold text-sm md:text-base text-gray-900">Community Driven</h4>
              <p className="text-[10px] md:text-xs text-gray-400">Managed with ❤️ by Vishal Kumar</p>
            </div>
          </div>
          <div className="flex flex-wrap justify-center gap-3 md:space-x-4">
            <div className="bg-orange-50 text-orange-700 px-3 md:px-4 py-1.5 md:py-2 rounded-full text-[10px] md:text-xs font-bold flex items-center">
              <CheckCircle2 size={12} className="md:w-[14px] md:h-[14px] mr-1.5 md:mr-2" /> Bihar Govt verified formats
            </div>
            <div className="bg-gray-100 text-gray-700 px-3 md:px-4 py-1.5 md:py-2 rounded-full text-[10px] md:text-xs font-bold">Updated weekly</div>
          </div>
        </div>
      </div>
    </div>
  );
};
